import {
  describedBy,
  FieldLabel,
  type FieldLabelVariant,
  fieldIds,
} from '@ctw/shared/components/_internal/form-field';
import { Badge } from '@ctw/shared/components/badge';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import type { IconDefinition } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import ReactSelect, { components, type OptionProps } from 'react-select';

type SelectInputLabelPosition = 'top' | 'left';

// 'compact' is the default dense control; 'field' matches `TextInput`'s height, padding and text,
// for forms that mix the two.
export type SelectInputVariant = 'compact' | 'field';

export interface SelectOption<TValue extends string = string> {
  label: string;
  subLabel?: string;
  /** Short badge text shown next to the label in the open menu, e.g. "Managed". */
  badge?: string;
  value: TValue;
}

export interface SelectInputProps {
  id?: string;
  testId?: string;
  name: string;
  label: string;
  initialValue?: string;
  required?: boolean;
  helpText?: string;
  disabled?: boolean;
  icon?: IconDefinition;
  placeholder?: string;
  isClearable?: boolean;
  labelPosition?: SelectInputLabelPosition;
  variant?: SelectInputVariant;
  // 'caps' renders a small uppercase label with `helpText` inline after it, on one line.
  labelVariant?: FieldLabelVariant;
  options: Array<SelectOption>;
  onChange?: (option: SelectOption | null) => void;
  className?: ClassName;
  labelClassName?: ClassName;
  inputClassName?: ClassName;
  /**
   * Render the dropdown menu into the provided element rather than inline.
   * Use this when SelectInput is rendered inside a container with `overflow`
   * clipping (e.g. a modal/dialog) to prevent the menu from being cut off.
   */
  menuPortalTarget?: HTMLElement | null;
}

export const SelectInput = ({
  id,
  testId,
  name,
  label,
  helpText,
  icon,
  required,
  disabled,
  initialValue,
  placeholder,
  isClearable,
  labelPosition = 'top',
  variant = 'compact',
  labelVariant = 'default',
  options,
  onChange: externalOnChange,
  className,
  labelClassName,
  inputClassName,
  menuPortalTarget,
}: SelectInputProps) => {
  const isField = variant === 'field';
  const { helpTextId } = fieldIds(name);
  const [selectedOption, setSelectedOption] = useState<SelectOption | undefined>(
    options.find((option) => option.value === initialValue),
  );

  const onChange = (option: SelectOption | null) => {
    externalOnChange?.(option);
    setSelectedOption(option ?? undefined);
  };

  return (
    <div
      id={id}
      data-testid={testId}
      className={twx(
        'flex shrink-0',
        {
          'flex-row items-center gap-2': labelPosition === 'left',
          'flex-col gap-1.5': labelPosition === 'top' && labelVariant !== 'caps',
          'flex-col gap-1': labelPosition === 'top' && labelVariant === 'caps',
        },
        className,
      )}
    >
      <FieldLabel
        htmlFor={name}
        label={label}
        variant={labelVariant}
        required={required}
        helpText={helpText}
        helpTextId={helpTextId}
        labelClassName={labelClassName}
      />
      <div className={twx('relative flex-1', inputClassName)}>
        {icon && (
          <div
            className={tw`pointer-events-none absolute inset-y-0 left-0 z-10 flex w-5 items-center justify-center`}
          >
            <FontAwesomeIcon icon={icon} className={tw`h-3 w-3 text-content-icon`} />
          </div>
        )}
        <ReactSelect
          unstyled={true}
          classNamePrefix="select-input"
          inputId={name}
          name={name}
          value={selectedOption}
          onChange={onChange as never}
          options={options as never}
          required={required}
          isDisabled={disabled}
          placeholder={placeholder}
          isClearable={isClearable}
          menuPlacement="bottom"
          menuPortalTarget={menuPortalTarget}
          aria-describedby={describedBy(helpText && helpTextId)}
          // The field variant shows only the chevron, like a native select.
          components={
            isField ? { Option: CustomOption, IndicatorSeparator: null } : { Option: CustomOption }
          }
          styles={{
            // Forced via inline styles because Tailwind utilities for these specific
            // properties get overriden by `react-select`
            control: (base) =>
              isField
                ? { ...base, height: 'auto', minHeight: 0 }
                : { ...base, height: '1.75rem', minHeight: '1.75rem' },
            // Boost the portalled menu above modal overlays. Tailwind's `z-menu`
            // utility doesn't apply to portalled elements since they live outside
            // the parent stacking context.
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            valueContainer: (base) => ({
              ...base,
              height: isField ? 'auto' : '100%',
              minWidth: 0,
              flexWrap: 'nowrap',
            }),
            input: (base) => ({ ...base, height: '100%', minWidth: 0 }),
            // In the field variant the control sizes to its content, so the indicators centre on
            // it rather than filling a fixed height.
            indicatorsContainer: (base) =>
              isField ? { ...base, alignSelf: 'center' } : { ...base, height: '100%' },
            singleValue: (base) => ({
              ...base,
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }),
            placeholder: (base) => ({
              ...base,
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }),
          }}
          classNames={{
            control: ({ isFocused, isDisabled }) =>
              isField
                ? twx(
                    'flex w-full cursor-pointer items-center rounded-md border bg-white py-2 pr-1 text-sm',
                    {
                      'pl-8': icon,
                      'pl-3': !icon,
                      'border-border-main': !isFocused,
                      'border-primary-main ring-1 ring-primary-main': isFocused,
                      'cursor-not-allowed bg-background-disabled text-content-disabled': isDisabled,
                    },
                  )
                : twx(
                    'flex h-5 min-h-0 w-full cursor-pointer items-center rounded border border-border-main bg-background-main pr-0.5 text-sm leading-none shadow-sm',
                    {
                      'pl-5': icon,
                      'pl-2': !icon,
                      'cursor-not-allowed bg-background-disabled': isDisabled,
                      'outline outline-2 outline-primary-main -outline-offset-1': isFocused,
                    },
                  ),
            valueContainer: () => 'flex h-full min-w-0 flex-1 items-center gap-1 overflow-hidden',
            placeholder: () =>
              isField
                ? 'min-w-0 max-w-full truncate text-content-disabled leading-5'
                : 'min-w-0 max-w-full truncate font-medium text-content-subtle leading-none',
            singleValue: () =>
              isField
                ? 'min-w-0 max-w-full truncate text-content-main leading-5'
                : 'min-w-0 max-w-full truncate font-medium text-content-main leading-none',
            input: () =>
              isField
                ? 'm-0 min-w-0 p-0 text-content-main leading-5'
                : 'm-0 min-w-0 p-0 font-medium text-content-main leading-none [&_input]:h-full',
            indicatorsContainer: () => 'flex h-full items-center',
            indicatorSeparator: () => 'my-0.5 w-px self-stretch bg-divider-main',
            dropdownIndicator: ({ isDisabled }) =>
              twx(
                'flex items-center px-1 text-content-icon transition-colors [&_svg]:h-3 [&_svg]:w-3',
                {
                  'hover:text-content-main': !isDisabled,
                },
              ),
            clearIndicator: () =>
              'flex cursor-pointer items-center px-1 text-content-icon transition-colors hover:text-content-main [&_svg]:h-3 [&_svg]:w-3',
            menu: () =>
              'absolute z-menu w-full overflow-hidden rounded-md border border-border-main bg-background-main shadow-lg',
            menuList: () => 'max-h-60 overflow-auto py-1',
            option: ({ isFocused, isSelected, isDisabled }) =>
              twx('cursor-pointer px-3 py-2 text-sm transition-colors', {
                'bg-background-hover': isFocused && !isSelected,
                'bg-primary-background text-primary-text': isSelected,
                'cursor-not-allowed text-content-disabled': isDisabled,
              }),
            noOptionsMessage: () => 'px-3 py-2 text-content-subtle text-sm italic',
            loadingMessage: () => 'px-3 py-2 text-content-subtle text-sm italic',
            groupHeading: () =>
              'px-3 pt-2 pb-1 font-medium text-content-subtle text-xs uppercase tracking-wide',
          }}
        />
      </div>
    </div>
  );
};

const CustomOption = (props: OptionProps<SelectOption>) => {
  return (
    <components.Option {...props}>
      <div className={tw`flex items-center justify-between gap-2`}>
        <div className={tw`min-w-0 flex-1`}>
          <div className={tw`truncate leading-tight`}>{props.data.label}</div>
          {props.data.subLabel && (
            <div className={tw`truncate text-content-subtle text-sm leading-tight`}>
              {props.data.subLabel}
            </div>
          )}
        </div>
        {props.data.badge && (
          <Badge variant="meta" content={props.data.badge} className={tw`shrink-0`} />
        )}
      </div>
    </components.Option>
  );
};
